import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Crown, CalendarDays, MapPin, Search, Plus, BadgeCheck } from 'lucide-react';
import { Input } from '../components/ui/input';
import Flag from '../components/world/Flag';
import SuggestDialog from '../components/world/SuggestDialog';
import { useJazyk } from '../context/LanguageContext';
import { API } from '../lib/api';

/**
 * Soutěže napříč celým světem.
 *
 * Soutěže šly dosud najít jen uvnitř konkrétní země, což je u nich k ničemu:
 * kdo se chce přihlásit, řeší termín a uzávěrku, ne to, ve které zemi zrovna
 * hledá. Tahle stránka je proto řadí podle data přes všechny země.
 *
 * Nahoře jsou ty, na které se dá ještě přihlásit — jinak by se listovalo
 * ročníky, které dávno skončily.
 */

const datum = (iso, jazyk) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(jazyk === 'en' ? 'en-GB' : 'cs-CZ',
      { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return ''; }
};

// Ročníky, na které se dá ještě něco stihnout, patří nahoru.
const OTEVRENE = ['registration_open', 'upcoming'];

export default function PageantsPage() {
  const { jazyk, t, dvojjazycne } = useJazyk();
  const [polozky, setPolozky] = useState([]);
  const [zeme, setZeme] = useState({});
  const [nacita, setNacita] = useState(true);
  const [hledani, setHledani] = useState('');
  const [navrhOtevren, setNavrhOtevren] = useState(false);

  useEffect(() => {
    let zivy = true;
    (async () => {
      try {
        const [s, z] = await Promise.all([
          axios.get(`${API}/directory/pageants`, { params: { limit: 100 } }),
          axios.get(`${API}/geo/countries`).catch(() => ({ data: [] })),
        ]);
        if (!zivy) return;
        setPolozky(s.data.items || []);
        setZeme(Object.fromEntries((z.data || []).map((c) => [c.iso2, c])));
      } catch {
        if (zivy) setPolozky([]);
      } finally {
        if (zivy) setNacita(false);
      }
    })();
    return () => { zivy = false; };
  }, []);

  const nalezene = useMemo(() => {
    const h = hledani.trim().toLowerCase();
    const filtr = h
      ? polozky.filter((p) => (p.search_text || p.name || '').toLowerCase().includes(h))
      : polozky;
    // Otevřené ročníky nahoru, uvnitř podle nejbližšího termínu.
    return [...filtr].sort((a, b) => {
      const oa = OTEVRENE.includes(a.details?.edition_status) ? 0 : 1;
      const ob = OTEVRENE.includes(b.details?.edition_status) ? 0 : 1;
      if (oa !== ob) return oa - ob;
      return (a.details?.starts_at || '9999').localeCompare(b.details?.starts_at || '9999');
    });
  }, [polozky, hledani]);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8" data-testid="pageants-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-bloom-text flex items-center gap-2">
            <Crown className="w-7 h-7 text-bloom-violet" />
            {t('sekce.pageants')}
          </h1>
          <p className="mt-1.5 text-bloom-sub text-sm sm:text-base">
            {t('souteze.podtitulek')}
          </p>
        </div>
        <button type="button" onClick={() => setNavrhOtevren(true)}
                className="inline-flex items-center gap-1.5 text-sm bg-white border border-border/50 text-bloom-sub rounded-xl px-3 py-2 hover:text-bloom-violet hover:border-bloom-violet/30"
                data-testid="souteze-navrhnout">
          <Plus className="w-4 h-4" /> {t('vypis.navrhnout')}
        </button>
      </header>

      <div className="relative mt-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-bloom-sub" />
        <Input value={hledani} onChange={(e) => setHledani(e.target.value)}
               placeholder={t('souteze.hledat')} className="pl-9"
               aria-label={t('obecne.hledat')} data-testid="souteze-hledani" />
      </div>

      {nacita ? (
        <div className="py-16 flex justify-center"><div className="spinner" /></div>
      ) : nalezene.length === 0 ? (
        <div className="py-14 text-center">
          <p className="text-bloom-sub">
            {hledani ? t('vypis.nicNeodpovida') : t('souteze.zadne')}
          </p>
          <button type="button" onClick={() => setNavrhOtevren(true)}
                  className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-white bg-bloom-violet hover:bg-bloom-violet/90 px-5 py-2.5 rounded-xl">
            <Plus className="w-4 h-4" /> {t('zeme.navrhnout')}
          </button>
        </div>
      ) : (
        <ul className="mt-5 grid gap-3 sm:grid-cols-2" data-testid="souteze-seznam">
          {nalezene.map((p) => {
            const z = zeme[p.location?.country_iso2];
            const stav = p.details?.edition_status;
            return (
              <li key={p.id}>
                <Link to={`/world/${z?.slug || p.location?.country_iso2}/pageants/${p.id}`}
                      className="block h-full bg-white border border-border/40 rounded-2xl p-4 hover:border-bloom-violet/40 hover:shadow-sm transition-all">
                  <div className="flex items-start gap-2">
                    <h2 className="font-heading font-bold text-bloom-text flex-1 min-w-0">
                      {p.name}
                    </h2>
                    {p.verification === 'verified' && (
                      <BadgeCheck className="w-4 h-4 text-bloom-mint shrink-0 mt-0.5" />
                    )}
                  </div>

                  {stav && (
                    <p className={`mt-1 text-xs font-medium ${
                      OTEVRENE.includes(stav) ? 'text-bloom-violet' : 'text-bloom-sub'}`}>
                      {t(`detail.rocnik.${stav}`)}
                    </p>
                  )}

                  <div className="mt-2.5 space-y-1 text-sm text-bloom-sub">
                    {z && (
                      <p className="flex items-center gap-1.5">
                        <Flag emoji={z.flag} iso2={z.iso2} size="sm" />
                        {dvojjazycne(z.name_cs, z.name_en)}
                        {p.location?.city_name ? ` · ${p.location.city_name}` : ''}
                      </p>
                    )}
                    {!z && p.location?.city_name && (
                      <p className="flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5" />{p.location.city_name}
                      </p>
                    )}
                    {p.details?.starts_at && (
                      <p className="flex items-center gap-1.5">
                        <CalendarDays className="w-3.5 h-3.5" />
                        {datum(p.details.starts_at, jazyk)}
                      </p>
                    )}
                    {p.details?.registration_deadline && (
                      <p className="text-xs">
                        {t('detail.uzaverka')}: {datum(p.details.registration_deadline, jazyk)}
                      </p>
                    )}
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      <SuggestDialog open={navrhOtevren} onOpenChange={setNavrhOtevren}
                     vychoziDruh="pageants" />
    </div>
  );
}
